import React, { createContext, useState, useContext, useEffect } from 'react';
import { loginApi, logoutApi, getMe } from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('dashboard_token');
    const storedUser = localStorage.getItem('dashboard_user');
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Verify token is still valid with the backend
        getMe(storedToken)
          .then((userData) => {
            setUser(userData);
            setToken(storedToken);
          })
          .catch(() => {
            // Token expired or invalid, clear session
            localStorage.removeItem('dashboard_token');
            localStorage.removeItem('dashboard_user');
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (e) {
        localStorage.removeItem('dashboard_token');
        localStorage.removeItem('dashboard_user');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    try {
      const data = await loginApi(username, password);
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('dashboard_token', data.token);
      localStorage.setItem('dashboard_user', JSON.stringify(data.user));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await logoutApi(token);
      }
    } catch (err) {
      console.warn('Logout API error:', err);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('dashboard_token');
      localStorage.removeItem('dashboard_user');
    }
  };

  const isAuthenticated = () => {
    return user !== null && token !== null;
  };

  const getToken = () => token;

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated, getToken, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
